import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import InstructorAnalytics from "@/components/InstructorAnalytics";
import CurriculumBuilder from "@/components/curriculum/CurriculumBuilder";
import AttendanceBoard from "@/components/attendance/AttendanceBoard";
import ProgramEditDialog from "@/components/admin/ProgramEditDialog";

export default function InstructorProgramEditor() {
    const { programId } = useParams();
    const [params] = useSearchParams();
    const initialTab = params.get("tab") || "curriculum";

    const [program, setProgram] = useState(null);
    const [students, setStudents] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [editOpen, setEditOpen] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get(`/programs/${programId}`);
        setProgram(data);
    }, [programId]);

    const loadStudents = useCallback(async () => {
        try {
            const { data } = await api.get(`/instructor/programs/${programId}/students`);
            setStudents(data);
        } catch {
            // Admins who aren't the instructor still get empty list; silent.
        }
    }, [programId]);

    useEffect(() => {
        load();
        loadStudents();
    }, [load, loadStudents]);

    const handleAIGenerate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/ai/generate-program-content", {
                program_id: programId,
                num_chapters: 2,
                lessons_per_chapter: 2,
            });
            toast.success(`AI generated ${data.chapters_created} chapter(s). Review below.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally {
            setGenerating(false);
        }
    };

    if (!program) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Loading…
            </div>
        );
    }

    const totalLessons = (program.chapters || []).reduce(
        (sum, ch) => sum + (ch.lessons?.length || 0),
        0,
    );
    const totalHours = (
        program.total_hours ??
        (program.chapters || []).reduce((s, c) => s + (Number(c.hours) || 1), 0)
    );

    return (
        <div className="max-w-7xl mx-auto px-6 py-10" data-testid="instructor-program-editor">
            <Link to="/instructor" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="mt-2 flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
                        Program
                    </div>
                    <h1 className="font-display text-3xl text-primary">{program.title}</h1>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="capitalize">
                            {program.level}
                        </Badge>
                        {program.subject && (
                            <Badge variant="outline" className="capitalize">
                                {program.subject}
                            </Badge>
                        )}
                        <span>·</span>
                        <span>
                            {program.chapters?.length || 0} chapter
                            {program.chapters?.length === 1 ? "" : "s"}
                        </span>
                        <span>·</span>
                        <span>{totalLessons} lesson{totalLessons === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span data-testid="program-total-hours">{totalHours.toFixed(1)} hour{totalHours === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>
                            ${Number(program.price || 0).toFixed(2)} CAD
                            {program.price_international
                                && Number(program.price_international) > 0 ? (
                                <span className="text-muted-foreground">
                                    {" · intl "}${Number(program.price_international).toFixed(2)}
                                </span>
                            ) : null}
                        </span>
                    </div>
                </div>
                <Button
                    variant="outline"
                    onClick={() => setEditOpen(true)}
                    className="gap-1.5"
                    data-testid="instructor-edit-details-btn"
                >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit details
                </Button>
            </div>

            <ProgramEditDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                program={program}
                instructors={[]}
                lockInstructor={true}
                endpoint={`/programs/${programId}`}
                onSaved={() => {
                    setEditOpen(false);
                    load();
                }}
            />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
                <TabsList>
                    <TabsTrigger value="curriculum" data-testid="tab-curriculum">
                        Curriculum
                    </TabsTrigger>
                    <TabsTrigger value="students" data-testid="tab-students">
                        Students
                    </TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="tab-analytics">
                        Analytics
                    </TabsTrigger>
                    <TabsTrigger value="attendance" data-testid="tab-attendance">
                        Attendance
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="curriculum" className="mt-6">
                    <CurriculumBuilder
                        program={program}
                        onRefresh={load}
                        onAIGenerate={handleAIGenerate}
                        generating={generating}
                    />
                </TabsContent>

                <TabsContent value="students" className="mt-6">
                    <Card className="border-border">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Progress</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {students.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                className="text-center text-muted-foreground py-8"
                                            >
                                                No students enrolled yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {students.map((s) => (
                                        <TableRow key={s.enrollment_id}>
                                            <TableCell className="font-medium">
                                                {s.student.name}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {s.student.email}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Progress
                                                        value={s.progress_percent}
                                                        className="h-2 w-28"
                                                    />
                                                    <span className="text-xs text-muted-foreground">
                                                        {s.progress_percent}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {s.completed_at ? (
                                                    <Badge className="bg-accent text-accent-foreground">
                                                        Completed
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline">In progress</Badge>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="analytics" className="mt-6">
                    {activeTab === "analytics" ? (
                        <InstructorAnalytics programId={programId} />
                    ) : null}
                </TabsContent>

                <TabsContent value="attendance" className="mt-6">
                    {activeTab === "attendance" && program ? (
                        <AttendanceBoard
                            programs={[{ id: program.id, title: program.title }]}
                            initialProgramId={program.id}
                            heading="Attendance"
                            subheading={`Who's studying ${program.title} and for how long.`}
                        />
                    ) : null}
                </TabsContent>
            </Tabs>
        </div>
    );
}
